import { useState, useEffect } from 'react';
import { useJwt, useAuthHeaders } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Badge, Button, Input, Modal, Select, Tabs } from '../ds/index.js';
import { AdminGuestLinks } from './AdminGuestLinks.js';
import { MemberImportModal } from './MemberImportModal.js';
import { PhotoImportModal } from './PhotoImportModal.js';
import { PersonAvatar } from '../people/PersonAvatar.js';
import { PhotoField } from '../people/PhotoField.js';
import { loadMembers } from '../people/membersStore.js';
import { AdminAccessProfiles, fetchAccessProfiles, type AccessProfile } from './AdminAccessProfiles.js';

interface Member {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  role: string;
  avatarIndex: number;
  joinedAt: string;
  lastSeenAt: string | null;
  bannedAt: string | null;
  hasPassword: boolean;
  photoVersion: string | null;
  /** Profil d'accès (ex. Copycampers), null = tout le campus. */
  accessProfileId: string | null;
  /** Accès « en plus » (profils `extra`, ex. Ascension). */
  extraAccessIds: string[];
}

function getTenantIdFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.tid as string) ?? '';
  } catch { return ''; }
}

function getCurrentUserId(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.sub as string) ?? '';
  } catch { return ''; }
}

function getCurrentRoleFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.role as string) ?? '';
  } catch { return ''; }
}

const CREATE_MEMBER_ERRORS: Record<string, string> = {
  EMAIL_ALREADY_EXISTS: 'Cette adresse a déjà un compte dans l\u2019espace.',
  INVALID_EMAIL: 'Cette adresse e-mail n\u2019est pas valide.',
  INVALID_ROLE: 'Choisis le rôle Membre ou Admin.',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

const ROLE_LABEL: Record<string, string> = { OWNER: 'Propriétaire', ADMIN: 'Admin', MEMBER: 'Membre', GUEST: 'Invité' };

/**
 * AdminMembers — Page de gestion des membres du tenant.
 */
export function AdminMembers() {
  const jwt = useJwt();
  const tenantId = getTenantIdFromJwt(jwt);
  const authHeaders = useAuthHeaders(tenantId);
  const currentUserId = getCurrentUserId(jwt);
  const isOwner = getCurrentRoleFromJwt(jwt).toUpperCase() === 'OWNER';
  const isAdmin = ['OWNER', 'ADMIN'].includes(getCurrentRoleFromJwt(jwt).toUpperCase());

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [tab, setTab] = useState<'members' | 'access' | 'links'>('members');
  const [profiles, setProfiles] = useState<AccessProfile[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('MEMBER');
  const [info, setInfo] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [photoFor, setPhotoFor] = useState<Member | null>(null);
  const [showPhotoImport, setShowPhotoImport] = useState(false);

  // Portraits (PersonAvatar) : annuaire du campus à jour.
  useEffect(() => { void loadMembers(0); }, []);

  const reloadProfiles = () => { if (isAdmin) fetchAccessProfiles().then(setProfiles).catch(() => setProfiles([])); };
  useEffect(reloadProfiles, [isAdmin]);

  const reloadMemberList = () => {
    fetch(`${API_BASE}/admin/members`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (Array.isArray(data)) setMembers(data); })
      .catch(() => { /* la liste se rechargera */ });
  };

  const limitProfiles = profiles.filter((p) => p.kind !== 'extra');
  const extraProfiles = profiles.filter((p) => p.kind === 'extra');

  async function handleExtraToggle(memberId: string, profileId: string) {
    const target = members.find((m) => m.id === memberId);
    const profile = profiles.find((p) => p.id === profileId);
    if (!target || !profile) return;
    const has = target.extraAccessIds.includes(profileId);
    if (!window.confirm(has
      ? `Retirer l\u2019accès « ${profile.name} » à ${target.displayName} ?\n\nIl ou elle sera déconnecté(e) pour en tenir compte.`
      : `Donner l\u2019accès « ${profile.name} » à ${target.displayName} ?\n\nIl ou elle sera déconnecté(e) pour en tenir compte.`)) return;
    const extraIds = has ? target.extraAccessIds.filter((id) => id !== profileId) : [...target.extraAccessIds, profileId];
    try {
      const res = await fetch(`${API_BASE}/admin/members/${memberId}/extra-access`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ extraIds }),
      });
      if (!res.ok) { setError('L\u2019accès n\u2019a pas pu être changé.'); return; }
      setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, extraAccessIds: extraIds } : m));
      reloadProfiles();
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    }
  }

  async function handleAccessChange(memberId: string, profileId: string | null) {
    const target = members.find((m) => m.id === memberId);
    const profile = profiles.find((p) => p.id === profileId);
    if (!window.confirm(profile
      ? `Limiter ${target?.displayName ?? 'ce membre'} au profil « ${profile.name} » ?\n\nIl ou elle sera déconnecté(e) pour en tenir compte.`
      : `Rendre l\u2019accès à tout le campus à ${target?.displayName ?? 'ce membre'} ?\n\nIl ou elle sera déconnecté(e) pour en tenir compte.`)) return;
    try {
      const res = await fetch(`${API_BASE}/admin/members/${memberId}/access-profile`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId }),
      });
      if (!res.ok) { setError('L\u2019accès n\u2019a pas pu être changé.'); return; }
      setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, accessProfileId: profileId } : m));
      reloadProfiles();
      void loadMembers(0);
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    }
  }

  useEffect(() => {
    if (!jwt || !tenantId) return;
    let cancelled = false;
    fetch(`${API_BASE}/admin/members`, { headers: authHeaders })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => { if (!cancelled) setMembers(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setError('Impossible de charger les membres.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jwt, tenantId]);

  async function handleRoleChange(memberId: string, newRole: string) {
    if (!jwt) return;
    const target = members.find((m) => m.id === memberId);
    const name = target?.displayName ?? 'ce membre';
    // Passer propriétaire ou retirer ce rôle : mêmes droits que toi, confirmation obligatoire.
    if (newRole === 'OWNER' && !window.confirm(
      `Passer ${name} propriétaire ?\n\nIl ou elle aura les mêmes droits que toi : créer et inviter des comptes, changer les rôles (y compris le tien), bannir. Il ou elle devra se reconnecter.`,
    )) return;
    if (target?.role === 'OWNER' && newRole !== 'OWNER' && !window.confirm(
      `Retirer le rôle de propriétaire à ${name} ?\n\nSon rôle deviendra « ${ROLE_LABEL[newRole] ?? newRole} » et il ou elle devra se reconnecter.`,
    )) return;
    try {
      const res = await fetch(`${API_BASE}/admin/members/${memberId}/role`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { message?: string };
        setError(err.message ?? 'L\u2019action n\u2019a pas abouti.');
        return;
      }
      // Un admin ou propriétaire n'a pas de profil d'accès (retiré par l'API).
      setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, role: newRole, accessProfileId: newRole === 'MEMBER' ? m.accessProfileId : null, extraAccessIds: newRole === 'MEMBER' ? m.extraAccessIds : [] } : m));
      reloadProfiles();
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    }
  }

  async function handleBan(memberId: string) {
    const target = members.find((m) => m.id === memberId);
    if (!jwt || !window.confirm(`Bannir ${target?.displayName ?? 'ce membre'} ? Il ou elle ne pourra plus se connecter à l\u2019espace.`)) return;
    try {
      const res = await fetch(`${API_BASE}/admin/members/${memberId}/ban`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { message?: string };
        setError(err.message ?? 'L\u2019action n\u2019a pas abouti.');
        return;
      }
      setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, bannedAt: new Date().toISOString() } : m));
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    }
  }

  async function handleCreateMember() {
    if (!jwt || creating) return;
    setCreating(true);
    setCreateError(null);
    setInfo(null);
    try {
      const res = await fetch(`${API_BASE}/admin/members`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail, role: newRole }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string; message?: string };
        setCreateError((err.error && CREATE_MEMBER_ERRORS[err.error]) ?? err.message ?? 'L\u2019invitation n\u2019a pas pu être envoyée.');
        return;
      }
      const created = await res.json() as Member & { emailSent: boolean };
      setMembers((prev) => [created, ...prev]);
      setInfo(created.emailSent
        ? `Invitation envoyée à ${created.email}.`
        : `Compte créé, mais l\u2019e-mail n\u2019a pas pu partir vers ${created.email}. Utilise « Renvoyer l\u2019invitation ».`);
      setShowCreateForm(false);
      setNewEmail('');
      setNewRole('MEMBER');
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    } finally {
      setCreating(false);
    }
  }

  async function handleResendInvite(memberId: string) {
    if (!jwt || resendingId) return;
    setResendingId(memberId);
    setError(null);
    setInfo(null);
    try {
      const res = await fetch(`${API_BASE}/admin/members/${memberId}/resend-invite`, {
        method: 'POST',
        headers: authHeaders,
      });
      const body = await res.json().catch(() => ({})) as { emailSent?: boolean; error?: string };
      if (!res.ok) {
        setError(body.error ?? 'L\u2019invitation n\u2019a pas pu être renvoyée.');
        return;
      }
      setInfo(body.emailSent ? 'Invitation renvoyée.' : 'L\u2019e-mail n\u2019a pas pu partir. Réessaie plus tard.');
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    } finally {
      setResendingId(null);
    }
  }

  async function handleUnban(memberId: string) {
    if (!jwt) return;
    try {
      const res = await fetch(`${API_BASE}/admin/members/${memberId}/unban`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!res.ok) return;
      setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, bannedAt: null } : m));
    } catch { /* ignore */ }
  }

  const filtered = members.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return m.displayName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
  });

  const pending = members.filter((m) => !m.hasPassword && !m.bannedAt).length;
  const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

  return (
    <>
      <header className="pl-page-head">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 className="pl-page-title">Membres</h1>
          <p className="pl-page-sub">
            {loading ? 'Chargement…' : plural(members.length, 'membre', 'membres')}
            {pending > 0 && ` · ${plural(pending, 'invitation en attente', 'invitations en attente')}`}
          </p>
        </div>
        {tab === 'members' && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ width: 280 }}>
              <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom ou e-mail" aria-label="Rechercher un membre" />
            </div>
            {isAdmin && <Button variant="secondary" onClick={() => { setInfo(null); setShowPhotoImport(true); }}>Importer des photos</Button>}
            {isOwner && <Button variant="secondary" onClick={() => { setInfo(null); setShowImport(true); }}>Importer une liste</Button>}
            {isOwner && <Button variant="primary" icon="plus" onClick={() => { setCreateError(null); setShowCreateForm(true); }}>Nouveau membre</Button>}
          </div>
        )}
      </header>

      <div><Tabs aria-label="Membres, accès ou liens invités" items={[
        { id: 'members', label: 'Membres' },
        ...(isAdmin ? [{ id: 'access' as const, label: 'Accès' }] : []),
        { id: 'links', label: 'Liens invités' },
      ]} value={tab} onChange={setTab} /></div>

      {tab === 'links' ? <AdminGuestLinks /> : tab === 'access' ? (
        <AdminAccessProfiles onChanged={() => { reloadProfiles(); reloadMemberList(); }} />
      ) : (
        <>
          {info && (
            <div className="pl-alert pl-alert-success" role="status" style={{ justifyContent: 'space-between' }}>
              <span>{info}</span>
              <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={() => setInfo(null)} />
            </div>
          )}
          {error && (
            <div className="pl-alert" role="alert" style={{ justifyContent: 'space-between' }}>
              <span>{error}</span>
              <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={() => setError(null)} />
            </div>
          )}

          {!loading && (
            <div className="pl-panel" style={{ overflow: 'hidden' }}>
              <table className="pl-table">
                <thead>
                  <tr><th>Membre</th><th>E-mail</th><th>Rôle</th>{profiles.length > 0 && <th>Accès</th>}<th>Statut</th><th>Inscription</th><th><span className="pl-sr">Actions</span></th></tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={profiles.length > 0 ? 7 : 6} className="pl-muted" style={{ textAlign: 'center', padding: 24 }}>{search ? 'Aucun membre ne correspond.' : 'Aucun membre pour l’instant.'}</td></tr>
                  )}
                  {filtered.map((m) => {
                    const isSelf = m.userId === currentUserId;
                    const targetIsOwner = m.role === 'OWNER';
                    const isBanned = !!m.bannedAt;
                    // Rôle : un OWNER gère tous les autres membres, propriétaires compris
                    // (jamais lui-même — il reste donc toujours un propriétaire).
                    const canChangeRole = isOwner && !isSelf;
                    // Bannir un propriétaire : lui retirer ce rôle d'abord.
                    const canManage = canChangeRole && !targetIsOwner;
                    return (
                      <tr key={m.id}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                            {isAdmin ? (
                              <button type="button" onClick={() => setPhotoFor(m)} title="Photo de profil" aria-label={`Photo de profil de ${m.displayName}`}
                                style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer', display: 'inline-flex', borderRadius: '50%' }}>
                                <PersonAvatar userId={m.userId} name={m.displayName} size={32} />
                              </button>
                            ) : <PersonAvatar userId={m.userId} name={m.displayName} size={32} />}
                            <span style={{ fontWeight: 500 }}>{m.displayName}</span>
                            {isSelf && <span className="pl-muted" style={{ fontSize: 12 }}>(toi)</span>}
                          </span>
                        </td>
                        <td className="pl-muted" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</td>
                        <td>
                          {canChangeRole && !isBanned ? (
                            <div style={{ width: 148 }}>
                              <Select
                                size="sm"
                                aria-label={`Rôle de ${m.displayName}`}
                                value={m.role}
                                options={[{ value: 'MEMBER', label: 'Membre' }, { value: 'ADMIN', label: 'Admin' }, { value: 'OWNER', label: 'Propriétaire' }]}
                                onChange={(e) => { void handleRoleChange(m.id, e.target.value); }}
                              />
                            </div>
                          ) : (
                            <Badge tone={targetIsOwner ? 'primary' : 'neutral'} mono>{ROLE_LABEL[m.role] ?? m.role}</Badge>
                          )}
                        </td>
                        {profiles.length > 0 && (
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                              {isAdmin && m.role === 'MEMBER' && !isBanned ? (
                                <>
                                  {limitProfiles.length > 0 && (
                                    <div style={{ width: 168 }}>
                                      <Select
                                        size="sm"
                                        aria-label={`Accès de ${m.displayName}`}
                                        value={m.accessProfileId ?? ''}
                                        options={[{ value: '', label: 'Tout le campus' }, ...limitProfiles.map((p) => ({ value: p.id, label: p.name }))]}
                                        onChange={(e) => { void handleAccessChange(m.id, e.target.value || null); }}
                                      />
                                    </div>
                                  )}
                                  {extraProfiles.length > 0 && (
                                    <div className="pl-choices" role="group" aria-label={`Accès en plus de ${m.displayName}`}>
                                      {extraProfiles.map((p) => (
                                        <button
                                          key={p.id}
                                          type="button"
                                          role="checkbox"
                                          aria-checked={m.extraAccessIds.includes(p.id)}
                                          className="pl-choice"
                                          style={{ height: 26, fontSize: 12 }}
                                          onClick={() => { void handleExtraToggle(m.id, p.id); }}
                                        >
                                          {p.name}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : m.role !== 'MEMBER' ? <span className="pl-muted">Tout le campus</span> : (
                                <>
                                  {m.accessProfileId ? <Badge>{profiles.find((p) => p.id === m.accessProfileId)?.name ?? 'Limité'}</Badge> : <span className="pl-muted">Tout le campus</span>}
                                  {m.extraAccessIds.map((id) => <Badge key={id} tone="primary">{profiles.find((p) => p.id === id)?.name ?? 'Accès'}</Badge>)}
                                </>
                              )}
                            </div>
                          </td>
                        )}
                        <td>
                          {isBanned ? <Badge tone="danger">Banni</Badge> : m.hasPassword ? <Badge tone="success">Actif</Badge> : <Badge tone="warning">Invitation en attente</Badge>}
                        </td>
                        <td className="pl-muted pl-num">{formatDate(m.joinedAt)}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            {isOwner && !m.hasPassword && !isBanned && (
                              <Button variant="ghost" size="sm" onClick={() => { void handleResendInvite(m.id); }} disabled={resendingId === m.id}>
                                {resendingId === m.id ? 'Envoi…' : 'Renvoyer l’invitation'}
                              </Button>
                            )}
                            {canManage && (isBanned
                              ? <Button variant="secondary" size="sm" onClick={() => { void handleUnban(m.id); }}>Débannir</Button>
                              : <Button variant="danger" size="sm" onClick={() => { void handleBan(m.id); }}>Bannir</Button>)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showPhotoImport && (
        <PhotoImportModal
          members={members.filter((m) => !m.bannedAt)}
          onClose={() => setShowPhotoImport(false)}
          onDone={(sent) => setInfo(sent > 0 ? `${sent} photo${sent > 1 ? 's' : ''} de profil enregistrée${sent > 1 ? 's' : ''}.` : null)}
        />
      )}

      {photoFor && (
        <Modal title={`Photo de profil · ${photoFor.displayName}`} onClose={() => setPhotoFor(null)}
          footer={<Button variant="primary" onClick={() => setPhotoFor(null)}>Terminé</Button>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <PhotoField userId={photoFor.userId} name={photoFor.displayName} endpoint={`/admin/members/${encodeURIComponent(photoFor.id)}/photo`} size={96} />
            <p className="pl-help" style={{ margin: 0 }}>Enregistrée tout de suite. {photoFor.displayName} pourra la changer dans « Profil et avatar ».</p>
          </div>
        </Modal>
      )}

      {showImport && (
        <MemberImportModal
          authHeaders={authHeaders}
          existingEmails={new Set(members.map((m) => m.email.toLowerCase()))}
          existingAccess={new Map(members.map((m) => [m.email.toLowerCase(), { role: m.role, profileId: m.accessProfileId, extraIds: m.extraAccessIds }]))}
          profiles={profiles}
          onClose={() => setShowImport(false)}
          onImported={() => { reloadMemberList(); reloadProfiles(); void loadMembers(0); }}
        />
      )}

      {showCreateForm && (
        <Modal
          title="Nouveau membre"
          onClose={() => setShowCreateForm(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowCreateForm(false)}>Annuler</Button>
              <Button variant="primary" onClick={() => { void handleCreateMember(); }} disabled={creating || !newEmail.trim()}>
                {creating ? 'Envoi…' : 'Envoyer l’invitation'}
              </Button>
            </>
          }
        >
          <form
            onSubmit={(e) => { e.preventDefault(); void handleCreateMember(); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16, color: 'var(--color-text-primary)' }}
          >
            <p className="pl-page-sub">
              La personne reçoit un e-mail pour choisir son mot de passe. Le lien est valable 24 heures.
              {' '}Plusieurs personnes ?{' '}
              <button type="button" onClick={() => { setShowCreateForm(false); setShowImport(true); }} style={{ padding: 0, border: 0, background: 'none', color: 'var(--color-primary-text)', font: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>Importer une liste</button>.
            </p>
            <Input
              label="Adresse e-mail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="prenom.nom@exemple.fr"
              autoFocus
              {...(createError ? { error: createError } : {})}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="pl-field-label">Rôle</span>
              <div><Tabs aria-label="Rôle" items={[{ id: 'MEMBER', label: 'Membre' }, { id: 'ADMIN', label: 'Admin' }]} value={newRole} onChange={setNewRole} /></div>
            </div>
            <p style={{ margin: 0, padding: 12, borderRadius: 'var(--radius-md)', background: 'var(--color-bg-raised)', fontSize: 12, lineHeight: 1.45, color: 'var(--color-text-secondary)' }}>
              Pour un accès ponctuel sans compte, crée plutôt un{' '}
              <button type="button" onClick={() => { setShowCreateForm(false); setTab('links'); }} style={{ padding: 0, border: 0, background: 'none', color: 'var(--color-primary-text)', font: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>lien invité</button>.
            </p>
          </form>
        </Modal>
      )}
    </>
  );
}
